const INITIALIZED_MENUS = new WeakSet();

class CustomNavMenuController {
  constructor(root) {
    this.root = root;
    this.root.addEventListener('keydown', this.handleKeydown);
    this.root.addEventListener('focusin', this.handleFocusIn);
    this.root.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('pointerdown', this.handlePointerDown);

    this.syncExpandedState();
  }

  handleKeydown = (event) => {
    const trigger = event.target.closest('.nav-menu__item-link');
    if (!(trigger instanceof HTMLAnchorElement) || !this.root.contains(trigger)) return;

    const item = trigger.closest('.nav-menu__item');
    const currentMenu = trigger.closest('.nav-menu, .nav-menu__submenu');

    if (!(item instanceof HTMLElement) || !(currentMenu instanceof HTMLElement)) return;

    const hasSubmenu = this.getSubmenu(item) !== null;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        if (currentMenu === this.root) {
          this.focusAdjacentItem(currentMenu, item, 1);
          return;
        }

        if (hasSubmenu) {
          this.openItem(item);
          this.focusFirstItem(this.getSubmenu(item));
          return;
        }

        this.focusAdjacentItem(currentMenu, item, 1);
        return;
      case 'ArrowLeft':
        event.preventDefault();
        if (currentMenu === this.root) {
          this.focusAdjacentItem(currentMenu, item, -1);
          this.closeItem(item);
          return;
        }

        this.closeParentMenu(currentMenu);
        return;
      case 'ArrowDown':
        event.preventDefault();
        if (hasSubmenu) {
          this.openItem(item);
          this.focusFirstItem(this.getSubmenu(item));
          return;
        }

        this.focusAdjacentItem(currentMenu, item, 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        if (currentMenu === this.root && hasSubmenu) {
          this.openItem(item);
          this.focusLastItem(this.getSubmenu(item));
          return;
        }

        this.focusAdjacentItem(currentMenu, item, -1);
        return;
      case 'Home':
        event.preventDefault();
        this.focusBoundaryItem(currentMenu, 'first');
        return;
      case 'End':
        event.preventDefault();
        this.focusBoundaryItem(currentMenu, 'last');
        return;
      case 'Escape':
        event.preventDefault();
        if (currentMenu === this.root) {
          this.closeItem(item);
          trigger.focus();
          return;
        }

        this.closeParentMenu(currentMenu);
        return;
      default:
        return;
    }
  };

  handleFocusIn = (event) => {
    const focusedLink = event.target.closest('.nav-menu__item-link');
    if (!(focusedLink instanceof HTMLAnchorElement) || !this.root.contains(focusedLink)) return;

    const focusedItem = focusedLink.closest('.nav-menu__item');
    if (!(focusedItem instanceof HTMLElement)) return;

    this.openItemChain(focusedItem);
  };

  handleFocusOut = () => {
    window.setTimeout(() => {
      if (this.root.contains(document.activeElement)) return;
      this.closeAll();
    }, 0);
  };

  handlePointerDown = (event) => {
    if (event.target instanceof Node && this.root.contains(event.target)) return;
    this.closeAll();
  };

  getMenuItems(menu) {
    if (!(menu instanceof HTMLElement)) return [];

    return Array.from(menu.children)
      .filter((child) => child instanceof HTMLElement && child.matches('.nav-menu__item'));
  }

  getTrigger(item) {
    return item?.querySelector(':scope > .nav-menu__item-link') ?? null;
  }

  getSubmenu(item) {
    return item?.querySelector(':scope > .nav-menu__submenu') ?? null;
  }

  focusAdjacentItem(menu, currentItem, direction) {
    const items = this.getMenuItems(menu);
    if (!items.length) return;

    const currentIndex = items.indexOf(currentItem);
    if (currentIndex === -1) return;

    const nextIndex = (currentIndex + direction + items.length) % items.length;
    const nextTrigger = this.getTrigger(items[nextIndex]);
    nextTrigger?.focus();
  }

  focusBoundaryItem(menu, boundary) {
    const items = this.getMenuItems(menu);
    if (!items.length) return;

    const targetItem = boundary === 'first' ? items[0] : items[items.length - 1];
    this.getTrigger(targetItem)?.focus();
  }

  focusFirstItem(menu) {
    this.focusBoundaryItem(menu, 'first');
  }

  focusLastItem(menu) {
    this.focusBoundaryItem(menu, 'last');
  }

  openItem(item) {
    if (!(item instanceof HTMLElement)) return;

    const parentMenu = item.parentElement;
    const isRootLevel = parentMenu === this.root;

    if (parentMenu instanceof HTMLElement) {
      this.getMenuItems(parentMenu).forEach((sibling) => {
        if (sibling !== item && (!isRootLevel || this.getSubmenu(sibling))) {
          this.closeItem(sibling);
        }
      });
    }

    item.classList.add('is-open');
    const trigger = this.getTrigger(item);
    if (trigger?.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  openItemChain(item) {
    let currentItem = item;

    while (currentItem instanceof HTMLElement && this.root.contains(currentItem)) {
      if (this.getSubmenu(currentItem)) {
        this.openItem(currentItem);
      }

      const parentMenu = currentItem.parentElement;
      if (parentMenu instanceof HTMLElement && parentMenu.matches('.nav-menu, .nav-menu__submenu')) {
        const parentItem = parentMenu.closest('.nav-menu__item');
        if (parentItem instanceof HTMLElement) {
          this.openItem(parentItem);
        }
      }

      if (!this.getSubmenu(currentItem)) {
        currentItem.classList.remove('is-open');
      }

      currentItem = parentMenu?.closest('.nav-menu__item');
    }
  }

  closeItem(item) {
    if (!(item instanceof HTMLElement)) return;

    item.classList.remove('is-open');
    const trigger = this.getTrigger(item);
    if (trigger?.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.getMenuItems(this.getSubmenu(item)).forEach((childItem) => this.closeItem(childItem));
  }

  closeParentMenu(currentMenu) {
    const parentItem = currentMenu.closest('.nav-menu__item');
    if (!(parentItem instanceof HTMLElement)) return;

    this.closeItem(parentItem);
    this.getTrigger(parentItem)?.focus();
  }

  closeAll() {
    this.getMenuItems(this.root).forEach((item) => this.closeItem(item));
  }

  syncExpandedState() {
    this.root.querySelectorAll('[data-nav-menu-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.setAttribute('aria-expanded', 'false');
    });
  }
}

function initCustomNavMenus(scope = document) {
  scope.querySelectorAll?.('[data-nav-menu]').forEach((menu) => {
    if (!(menu instanceof HTMLElement) || INITIALIZED_MENUS.has(menu)) return;

    new CustomNavMenuController(menu);
    INITIALIZED_MENUS.add(menu);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCustomNavMenus(), { once: true });
} else {
  initCustomNavMenus();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  initCustomNavMenus(event.target);
});